import { useState, type FormEvent } from "react";
import { siteStats } from "@/mocks/evidence";

type HeroProps = {
  onSearch: (query: string) => void;
};

const quickChips = ["聊天截图", "同框照", "定位信息", "时间戳画面"];

export default function Hero({ onSearch }: HeroProps) {
  const [value, setValue] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearch(value.trim());
    document.getElementById("archive")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative overflow-hidden pt-24 md:pt-28">
      <div className="paper-grain pointer-events-none absolute inset-0 opacity-70" />
      <div className="relative w-full px-4 pb-16 pt-8 md:px-6 md:pb-24">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="anim-rise lg:col-span-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 font-label text-xs tracking-wide text-primary-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-folder-shield-2-line text-sm" />
              </span>
              证据型照片 · 资源入口
            </span>

            <h2 className="mt-5 font-heading text-4xl font-black leading-tight text-foreground-950 md:text-[56px] md:leading-[1.08]">
              黑料照片
              <span className="mt-2 block text-2xl font-bold text-primary-600 md:text-3xl">
                按事件与时间线归档的实锤素材
              </span>
            </h2>

            <p className="mt-5 max-w-xl text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
              这里专整理带实锤性质的证据型照片，包括
              <strong className="mx-1 font-semibold text-foreground-900">聊天截图</strong>、
              <strong className="mx-1 font-semibold text-foreground-900">同框照</strong>、
              定位信息与时间戳画面。每张图都标注来源与关联事件，方便对照时间线判断可信度。
              想找某件事的实锤图或佐证素材，直接按事件名检索即可。
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-7 flex w-full flex-col gap-3 sm:flex-row sm:items-center"
            >
              <div className="flex flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-3">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-search-line text-lg" />
                </span>
                <input
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  placeholder="输入事件名，如「跨年商圈合影事件」"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 md:text-base"
                />
              </div>
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:text-base"
              >
                检索实锤图
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="font-label text-xs text-foreground-500">热门类型</span>
              {quickChips.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => {
                    onSearch(chip);
                    document.getElementById("archive")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-100/70 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-secondary-300 hover:bg-secondary-100 hover:text-secondary-900"
                >
                  {chip}
                </button>
              ))}
            </div>

            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-background-200 pt-6 sm:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.id}>
                  <dt className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                    {stat.value}
                  </dt>
                  <dd className="mt-1 font-label text-xs text-foreground-500">{stat.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="anim-rise lg:col-span-6">
            <div className="relative h-[420px] w-full overflow-hidden rounded-xl border border-background-200 md:h-[560px]">
              <img
                src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20flat%20lay%20of%20an%20investigation%20evidence%20board%20with%20pinned%20photographs%20connected%20by%20red%20string%2C%20warm%20amber%20lighting%2C%20textured%20kraft%20paper%20background%2C%20vintage%20dossier%20aesthetic%2C%20soft%20shadows%2C%20muted%20vermillion%20accents%2C%20high%20detail%2C%20magazine%20cover%20composition&width=1100&height=1300&seq=hl-hero-board-01&orientation=portrait"
                alt="黑料照片 证据板 实锤照片 归档 同框照 时间戳"
                title="黑料照片 · 证据型照片归档板"
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/45 via-transparent to-foreground-950/10" />

              <span className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-background-50/95 px-3 py-1.5 font-label text-xs text-foreground-800">
                <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-500">
                  <i className="ri-record-circle-line text-sm" />
                </span>
                资料库 · 持续更新
              </span>

              <div className="absolute inset-x-4 bottom-4 flex flex-col gap-3 sm:flex-row sm:items-stretch">
                <div className="flex flex-1 items-center gap-3 rounded-lg bg-background-50/95 px-4 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-image-2-line text-lg" />
                  </span>
                  <span className="flex flex-col">
                    <span className="font-heading text-lg font-black text-foreground-950">+12</span>
                    <span className="font-label text-[11px] text-foreground-500">今日新增图组</span>
                  </span>
                </div>
                <div className="flex flex-1 items-center gap-3 rounded-lg bg-background-50/95 px-4 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-verified-badge-line text-lg" />
                  </span>
                  <span className="flex flex-col">
                    <span className="font-heading text-lg font-black text-foreground-950">92%</span>
                    <span className="font-label text-[11px] text-foreground-500">带来源标注</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}